import { get, has } from 'lodash'
import {
  COUNTRY_SHORTCODES,
  PR_MARKET_ID,
  SupplyAcquisitionMethods,
} from '../constants'
import routes from '../routes'
import { isCaviarDomain } from '../utilities/window'
import * as Exp from '../experimentConstants'
import * as H from 'history'

// <Step> : <path param>
export const enum Steps {
  PhoneVerification = 'verify',
  ProfileSetup = 'profile',
  ApplicationClosed = 'applicationclosed',
  BackgroundCheck = 'backgroundcheck',
  BackgroundCheckAwait = 'backgroundcheckawait',
  GetSupplies = 'getsupplies',
  GetSuppliesV2 = 'getsuppliesv2',
  SignW9 = 'signw9',
  SignW9V2 = 'signw9v2',
  MailingAddress = 'mailingaddress',
  MailingAddressV2 = 'mailingaddressv2',
  Orientation = 'orientation',
  DropshippingConfirm = 'dropshipping',
  IdentityVerificationAwait = 'identityverificationawait',
}

export type stepType = {
  [x: string]: { path: Steps; includeMobileHeader?: boolean }
}

// STEPS configuration { path, includeHeader, isDSJ }
export const STEPS: stepType = {
  [Steps.PhoneVerification]: {
    path: Steps.PhoneVerification,
  },
  [Steps.ProfileSetup]: {
    path: Steps.ProfileSetup,
  },
  [Steps.ApplicationClosed]: {
    path: Steps.ApplicationClosed,
  },
  [Steps.BackgroundCheck]: {
    path: Steps.BackgroundCheck,
  },
  [Steps.BackgroundCheckAwait]: {
    path: Steps.BackgroundCheckAwait,
  },
  [Steps.GetSupplies]: {
    path: Steps.GetSupplies,
  },
  [Steps.GetSuppliesV2]: {
    path: Steps.GetSuppliesV2,
  },
  [Steps.SignW9]: {
    path: Steps.SignW9,
  },
  [Steps.SignW9V2]: {
    path: Steps.SignW9V2,
    includeMobileHeader: false,
  },
  [Steps.MailingAddress]: {
    path: Steps.MailingAddress,
  },
  [Steps.MailingAddressV2]: {
    path: Steps.MailingAddressV2,
    includeMobileHeader: false,
  },
  [Steps.Orientation]: {
    path: Steps.Orientation,
  },
  [Steps.DropshippingConfirm]: {
    path: Steps.DropshippingConfirm,
  },
  [Steps.IdentityVerificationAwait]: {
    path: Steps.IdentityVerificationAwait,
  },
}

const defaultStep = Steps.ProfileSetup

// get matching step, or default
export const stepMatch = step =>
  step in STEPS ? STEPS[step] : STEPS[defaultStep]

export const push = (uniqueLink, step, history?: H.History) => {
  const { path } = STEPS[step]
  if (history) {
    const pathname = routes.dasherflow.buildPath({
      uniqueLink,
      step: path,
    })
    history.push(pathname)
  } else {
    let pathname = routes.dasherflow.buildPath({
      uniqueLink,
      step: path,
    })

    // if we're on caviar branded landing page, use a full reference
    if (isCaviarDomain()) {
      pathname = `https://www.doordash.com${pathname}`
    }

    window.location.assign(pathname)
  }
}

export enum BGCResult {
  consider = 'consider',
  suspended = 'suspended',
}

export enum IDVStatus {
  NOT_COMPLETED = 'unspecified',
  COMPLETED = 'completed',
  FAILED = 'failed',
  STARTED = 'started',
  CREATED = 'created',
}

/**
 * determineCurrentStep
 *
 * @param application
 * @param experiments
 * @param uniqueLink
 * @param vargs [var args, arg1, arg2, ...]
 */
export const determineCurrentStep = (
  application,
  experiments,
  uniqueLink,
  { supplyAcquisitionMethod, mailingAddressSubmitted, isAtCapacity, idvStatus }
) => {
  const failedBackgroundCheck =
    get(application, 'backgroundCheckResults', null) === BGCResult.consider ||
    get(application, 'backgroundCheckResults', null) === BGCResult.suspended
  const failedMVRCheck =
    get(application, 'mvReportResults', null) === BGCResult.consider ||
    get(application, 'mvReportResults', null) === BGCResult.suspended
  const dropshippingAB = get(experiments, 'dropshipping_ab', false)
  const dropshipping = get(experiments, 'dropshipping', true)
  const dropshippingSkipSelection = get(
    experiments,
    'dropshipping_skip_selection',
    false
  )
  const uiRefresh = get(experiments, 'dasher_signup_ui_refresh', false)
  const isInNoW9Experiment = get(experiments, Exp.NO_W9_EXPERIMENT, false)
  const isInAddressCollectionAfterDashExperiment = get(
    experiments,
    Exp.ADDRESS_COLLECTION_AFTER_DASH,
    false
  )
  const isInInstantDashing = get(experiments, Exp.INSTANT_DASHING_V2, false)
  const phoneVerification = get(
    experiments,
    Exp.DX_PHONE_NUMBER_VERIFICATION_SIGNUP,
    false
  )
  const identityVerification = get(
    experiments,
    Exp.IDENTITY_VERIFICATION,
    false
  )

  const isAU = get(application, 'country', '') === COUNTRY_SHORTCODES.AU
  const isJP = get(application, 'country', '') === COUNTRY_SHORTCODES.JP

  if (application) {
    if (get(application, 'country', false) === 'CA') {
      return determineCurrentStepCanada(application, experiments, uniqueLink, {
        supplyAcquisitionMethod,
        mailingAddressSubmitted,
        isAtCapacity,
      })
    }

    if (get(application, 'market', 0) === PR_MARKET_ID) {
      return determineCurrentStepPuertoRico(
        application,
        experiments,
        uniqueLink,
        { isAtCapacity }
      )
    }

    if (
      identityVerification &&
      (application.identityVerificationStatus === IDVStatus.FAILED ||
        idvStatus.value === IDVStatus.FAILED)
    ) {
      return STEPS[Steps.IdentityVerificationAwait]
    }

    if (
      application.w9Signed ||
      (isInNoW9Experiment && mailingAddressSubmitted)
    ) {
      return STEPS[Steps.DropshippingConfirm]
    }

    if (
      phoneVerification &&
      has(application, 'isPhoneNumberVerified') &&
      application.isPhoneNumberVerified === false
    ) {
      return STEPS[Steps.PhoneVerification]
    }

    if (
      has(application, 'completedProfileStep') &&
      application.completedProfileStep === false
    ) {
      return STEPS[Steps.ProfileSetup]
    }

    const hasDasher = !!application.dasherId
    const hasDob = !!application.dateOfBirth
    const needsBgCheckInfo = !!get(application, 'needsBgCheckInfo', null)
    // Don't check needsBgCheckInfo for AU, JP
    if ((isAU || isJP) && (!hasDasher || !hasDob)) {
      if (isAtCapacity) {
        return STEPS[Steps.ApplicationClosed]
      }
      return STEPS[Steps.BackgroundCheck]
    }

    if ((needsBgCheckInfo || !hasDasher || !hasDob) && !isJP) {
      if (isAtCapacity) {
        return STEPS[Steps.ApplicationClosed]
      }
      return STEPS[Steps.BackgroundCheck]
    }

    if (
      (has(application, 'dasherOrientation') &&
        application.dasherOrientation == null &&
        dropshippingAB) ||
      (!dropshippingAB && dropshipping === false) ||
      (dropshippingSkipSelection === false &&
        supplyAcquisitionMethod === SupplyAcquisitionMethods.GO_INTO_OFFICE) ||
      !!application.dasherOrientation ||
      (application.dasherOrientation === null && isJP)
    ) {
      return STEPS[Steps.Orientation]
    }

    if (
      has(application, 'dasherOrientation') &&
      application.dasherOrientation == null &&
      dropshippingAB === false &&
      dropshipping === true
    ) {
      if (mailingAddressSubmitted) {
        return uiRefresh === true ? STEPS[Steps.SignW9V2] : STEPS[Steps.SignW9]
      }

      if (isInAddressCollectionAfterDashExperiment && isInInstantDashing) {
        return STEPS[Steps.DropshippingConfirm]
      }

      if (
        dropshippingSkipSelection === true ||
        supplyAcquisitionMethod === SupplyAcquisitionMethods.DROPSHIPPING
      ) {
        return uiRefresh === true
          ? STEPS[Steps.MailingAddressV2]
          : STEPS[Steps.MailingAddress]
      }
      return uiRefresh === true
        ? STEPS[Steps.GetSuppliesV2]
        : STEPS[Steps.GetSupplies]
    }
  }
  return false
}

const determineCurrentStepPuertoRico = (
  application,
  experiments,
  _,
  { isAtCapacity }
) => {
  const scheduledOrientation = get(application, 'orientationDate', null) != null
  const phoneVerification = get(
    experiments,
    Exp.DX_PHONE_NUMBER_VERIFICATION_SIGNUP,
    false
  )
  const completedProfileStep = get(application, 'completedProfileStep', false)
  const completedBackgroundStep =
    get(application, 'dateOfBirth') != null &&
    get(application, 'vehicleType') != null

  if (
    scheduledOrientation ||
    (completedProfileStep && completedBackgroundStep)
  ) {
    return STEPS[Steps.Orientation]
  }

  if (completedProfileStep) {
    if (isAtCapacity) {
      return STEPS[Steps.ApplicationClosed]
    }
    return STEPS[Steps.BackgroundCheck]
  } else if (
    phoneVerification &&
    has(application, 'isPhoneNumberVerified') &&
    application.isPhoneNumberVerified === false
  ) {
    return STEPS[Steps.PhoneVerification]
  }

  return STEPS[Steps.ProfileSetup]
}

const determineCurrentStepCanada = (
  application,
  experiments,
  _,
  { supplyAcquisitionMethod, mailingAddressSubmitted, isAtCapacity }
) => {
  const caDropshippingEnabled = get(
    experiments,
    'canada_dropshipping_embed_checkr_flow',
    false
  )
  const dropshippingSkipSelection = get(
    experiments,
    'dropshipping_skip_selection',
    false
  )

  // useDropshipping is true once an applicant
  // has submitted address for dropshipping
  const submittedDropshipping = get(application, 'useDropshipping', false)
  // useOrientation is true once an applicant
  // has selected an in-person orientation
  const scheduledOrientation = get(application, 'orientationDate', null) != null
  const phoneVerification = get(
    experiments,
    Exp.DX_PHONE_NUMBER_VERIFICATION_SIGNUP,
    false
  )
  const completedProfileStep = get(application, 'completedProfileStep', false)
  const completedBackgroundStep =
    get(application, 'dateOfBirth') != null &&
    get(application, 'vehicleType') != null

  // show dropship confirm if mailing address submitted
  // (coincides with useDropshipping=true on backend)
  if (submittedDropshipping && mailingAddressSubmitted) {
    // until background check is complete, we want to show
    // mailing address page with checkr modal
    if (application.backgroundCheckResults === 'incomplete') {
      return STEPS[Steps.MailingAddress]
    }
    return STEPS[Steps.DropshippingConfirm]
  }

  // show orientation confirm if orientation scheduled
  if (scheduledOrientation) {
    return STEPS[Steps.Orientation]
  }
  if (completedProfileStep && completedBackgroundStep) {
    if (!caDropshippingEnabled) {
      return STEPS[Steps.Orientation]
    }
    if (
      dropshippingSkipSelection ||
      supplyAcquisitionMethod === SupplyAcquisitionMethods.DROPSHIPPING
    ) {
      return STEPS[Steps.MailingAddress]
    }

    if (supplyAcquisitionMethod === SupplyAcquisitionMethods.GO_INTO_OFFICE) {
      return STEPS[Steps.Orientation]
    }

    return STEPS[Steps.GetSupplies]
  }

  if (completedProfileStep) {
    if (isAtCapacity) {
      return STEPS[Steps.ApplicationClosed]
    }
    return STEPS[Steps.BackgroundCheck]
  } else if (
    phoneVerification &&
    has(application, 'isPhoneNumberVerified') &&
    application.isPhoneNumberVerified === false
  ) {
    return STEPS[Steps.PhoneVerification]
  }

  return STEPS[Steps.ProfileSetup]
}
